import { useRouter } from 'next/router';

import { Layout_WithUser } from '@components/WithUser/WithUser';
import { NextPageWithLayout } from 'pages/_app';
import { useUser } from '@context/user-context';
import { Box, Loader } from '@ui-lib';
import { useActorCardsAndProfilesQuery } from './[cardId]/preview/actor-cards-profiles.graphql';

import { NoCardsMessage } from '../components/NoCardsMessage/NoCardsMessage';
import PersonalCardsLayout from './layout';
import { LoggedInPremiumUpgradeDialog } from '@components/LoggedInPremiumUpgradeDialog/LoggedInPremiumUpgradeDialog';
import { useEffect, useMemo } from 'react';

/** Find the first card and redirect to the preview route for that card */
export const PersonalCardListPage: NextPageWithLayout = () => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const router = useRouter();
  const { user, loading: userLoading } = useUser();

  const {
    data,
    called,
    loading: actorCardsAndProfilesLoading,
  } = useActorCardsAndProfilesQuery({
    variables: {
      timezone,
    },
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (actorCardsAndProfilesLoading || userLoading) {
      return;
    }

    const firstCardId = data?.actor?.cards?.edges?.[0]?.node?.id;
    if (!firstCardId) {
      return;
    }

    // Wait for user to load before attempting redirect to card preview
    if (userLoading) {
      return;
    }

    const currentWorkspace = user?.bbOrgId ?? user?.userId;
    const firstCardPreviewRoute = `/${currentWorkspace}/cards/personal/${firstCardId}/preview`;

    router.push(firstCardPreviewRoute);
  }, [router, user?.bbOrgId, timezone, user?.userId, data, userLoading, actorCardsAndProfilesLoading]);

  const showNoCardsMessage = useMemo(() => {
    if (!called) {
      return false;
    }

    if (actorCardsAndProfilesLoading) {
      return false;
    }

    if (data?.actor?.cards?.edges?.length && data?.actor?.cards?.edges?.length > 0) {
      return false;
    }

    return true;
  }, [called, actorCardsAndProfilesLoading, data]);

  if (showNoCardsMessage) {
    return (
      <Box display="flex" flexGrow={1}>
        <NoCardsMessage route="personal" />
      </Box>
    );
  }

  return (
    <Box display="flex" alignItems="center" justifyContent="center" width="full">
      <Loader />
    </Box>
  );
};

// eslint-disable-next-line react/display-name
PersonalCardListPage.getLayout = (page: React.ReactElement) => {
  return (
    <Layout_WithUser>
      <PersonalCardsLayout>{page}</PersonalCardsLayout>
      <LoggedInPremiumUpgradeDialog />
    </Layout_WithUser>
  );
};

export default PersonalCardListPage;
